import type { Mention, RichDocument, RichNode } from '@cyphex/types';

/**
 * Flattens a rich document to the plain projection the server keeps in
 * `bodyText` — used for previews, notifications, search and a11y labels.
 */
export function richToText(doc: RichDocument | null): string {
  if (!doc) return '';
  const parts: string[] = [];

  const walk = (node: RichNode): void => {
    if (node.type === 'text' && node.text) parts.push(node.text);
    else if (node.type === 'mention') parts.push(`@${node.attrs?.label ?? ''}`);
    else if (node.type === 'emoji') parts.push(node.attrs?.shortcode ?? '');
    else if (node.type === 'hard_break') parts.push('\n');
    node.content?.forEach(walk);
    if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'code_block') {
      parts.push('\n');
    }
  };

  doc.content.forEach(walk);
  return parts.join('').replace(/\n{3,}/g, '\n\n').trim();
}

/** Collects every mention node so the send request carries them explicitly. */
export function collectMentions(doc: RichDocument | null): Mention[] {
  if (!doc) return [];
  const found: Mention[] = [];

  const walk = (node: RichNode): void => {
    if (node.type === 'mention') {
      const label = node.attrs?.label ?? '';
      const kind = node.attrs?.mentionKind ?? 'user';
      found.push({ kind, id: node.attrs?.mentionId ?? null, label });
    }
    node.content?.forEach(walk);
  };

  doc.content.forEach(walk);
  return found;
}

/** One-line, 180-char preview for the conversation list. */
export function previewText(value: string, limit = 180): string {
  const single = value.replace(/\s+/g, ' ').trim();
  return single.length > limit ? `${single.slice(0, limit - 1)}…` : single;
}
